import { Calendar, Cookie, Eye, FileText, Shield } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useConfig } from '../hooks/useConfig';

/**
 * NOTE (Non‑legal disclaimer):
 * The copy below is designed to be practical and Canada/Ontario‑aware (PIPEDA & CASL references),
 * with additional global coverage (GDPR/UK GDPR, CCPA/CPRA, LGPD, ePrivacy, etc.).
 * It is not legal advice. Please have counsel review before publishing.
 */

const LegalPages = () => {
  const { site } = useConfig();
  const [activeTab, setActiveTab] = useState('terms');

  const tabs = [
    { id: 'terms', label: 'Terms of Service', icon: FileText },
    { id: 'privacy', label: 'Privacy Policy', icon: Eye },
    { id: 'security', label: 'Data Security', icon: Shield },
    { id: 'cookies', label: 'Cookie Policy', icon: Cookie }
  ];

  const lastUpdated = site?.legal?.lastUpdated || 'YYYY-MM-DD';
  const company = site?.company?.name || 'Booker.chat';
  const companyAddress = site?.company?.address || 'Ontario, Canada';
  const legalEmail = site?.contact?.legalEmail || 'legal@example.com';
  const securityEmail = site?.contact?.securityEmail || 'security@example.com';
  const supportPhone = site?.contact?.supportPhone || '+1 (000) 000-0000';
  const websiteUrl = site?.company?.url || 'https://booker.chat';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Calendar className="h-8 w-8 text-blue-600" />
              <span className="ml-2 text-xl font-bold text-gray-900">{company}</span>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                <Link to="/support" className="text-gray-500 hover:text-gray-900 px-3 py-2 text-sm font-medium">Support</Link>
                <Link to="/support" className="text-gray-500 hover:text-gray-900 px-3 py-2 text-sm font-medium">Contact</Link>
                <Link to="/register" className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
                  Start Free Trial
                </Link>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="bg-white py-12">
        <div className="max-w-4xl mx-auto text-left px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900">Legal Information</h1>
          <p className="mt-4 text-lg text-gray-600">
            Our commitment to transparency, privacy, and security
          </p>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="mr-2 h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow p-8 text-left">
            {activeTab === 'terms' && (
              <div className="prose max-w-none text-left">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Terms of Service</h2>
                <p className="text-gray-600 mb-6">
                  <strong>Effective Date:</strong> {lastUpdated}<br />
                  <strong>Last Updated:</strong> {lastUpdated}
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h3>
                <p className="text-gray-700 mb-6">
                  These Terms of Service (the "Terms") govern your access to and use of {company}'s products and services, including our websites, web and mobile applications, APIs, and integrations (collectively, the "Service"). By accessing or using the Service, you agree to be bound by these Terms and our Privacy Policy. If you are using the Service on behalf of a business, you represent that you have authority to bind that business to these Terms.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">2. Description of Service</h3>
                <p className="text-gray-700 mb-6">
                  {company} provides AI‑powered booking automation for appointment‑based businesses. Features may include WhatsApp and SMS messaging, phone call handling/IVR, calendar synchronization, availability and reminder workflows, analytics, and related tools as described on {websiteUrl}. We may add, change, or discontinue features from time to time.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">3. Eligibility & Accounts</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                  <li>You must be at least the age of majority in your province/territory (18 or 19, as applicable in Canada) to use the Service.</li>
                  <li>You are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account.</li>
                  <li>You must provide accurate information and keep it up to date.</li>
                </ul>

                <h3 className="text-xl font-bold text-gray-900 mb-4">4. Customer Data & Roles</h3>
                <p className="text-gray-700 mb-4">
                  "Customer Data" means any data you or your end‑customers submit to the Service (e.g., contact details, messages, appointments, availability, pricing, staff info). As between you and {company}, you retain all rights to Customer Data. To provide the Service, you grant {company} a worldwide, non‑exclusive, royalty‑free license to host, process, transmit, and display Customer Data.
                </p>
                <p className="text-gray-700 mb-6">
                  For privacy compliance: (a) you are the controller of Customer Data you input, and {company} acts as a service provider/processor under applicable laws (including PIPEDA and, where applicable, GDPR/UK GDPR/CCPA/CPRA/LGPD equivalents); (b) you are responsible for obtaining all necessary consents (e.g., CASL consent for commercial electronic messages and, if applicable, TCPA consent in the U.S.).
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">5. Acceptable Use</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                  <li>No unlawful, harmful, harassing, or deceptive activity.</li>
                  <li>No sending spam or unsolicited messages without valid consent (CASL). Provide clear opt‑out instructions.</li>
                  <li>No attempts to circumvent security, probe systems, or abuse APIs.</li>
                  <li>No infringement of third‑party rights, including privacy, publicity, or intellectual property.</li>
                  <li>Comply with WhatsApp/Twilio/telecom provider policies and applicable laws for messaging and calling.</li>
                </ul>

                <h3 className="text-xl font-bold text-gray-900 mb-4">6. AI/Automation Notice</h3>
                <p className="text-gray-700 mb-6">
                  Parts of the Service leverage automated decisioning and large language models to draft responses, classify intents, and schedule appointments. Outputs may be imperfect. You are responsible for reviewing critical communications and for any decisions made based on such outputs.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">7. Third‑Party Services & Integrations</h3>
                <p className="text-gray-700 mb-6">
                  The Service integrates with third‑party platforms (e.g., WhatsApp/Twilio, Google Calendar, payment processors). Your use of those services is governed by their terms and policies. We are not responsible for third‑party services.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">8. Payment Terms</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                  <li>Subscription fees are billed monthly or annually in advance. Taxes may apply.</li>
                  <li>All fees are non‑refundable except as expressly stated in these Terms or required by law.</li>
                  <li>We offer a {site?.pricing?.trialDays || '14'}‑day free trial. Trials may be limited or revoked for misuse.</li>
                  <li>We may change pricing with at least 30 days’ notice. Continued use after the change constitutes acceptance.</li>
                  <li>Failed or overdue payments may result in suspension or termination of the Service.</li>
                </ul>

                <h3 className="text-xl font-bold text-gray-900 mb-4">9. Service Changes; Beta Features</h3>
                <p className="text-gray-700 mb-6">
                  We may modify or discontinue the Service or any part thereof. We may offer beta or preview features on an "as is" basis with reduced or different support and reliability.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">10. Confidentiality</h3>
                <p className="text-gray-700 mb-6">
                  Each party agrees to protect the other party’s confidential information using reasonable safeguards and to use it solely for the purposes of these Terms. Customer Data is your confidential information.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">11. Intellectual Property</h3>
                <p className="text-gray-700 mb-6">
                  We and our licensors retain all rights in the Service and related IP. Feedback you provide may be used to improve the Service without restriction.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">12. Warranties; Disclaimers</h3>
                <p className="text-gray-700 mb-6">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE". TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON‑INFRINGEMENT. WE DO NOT GUARANTEE UNINTERRUPTED OR ERROR‑FREE OPERATION.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">13. Limitation of Liability</h3>
                <p className="text-gray-700 mb-6">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, {company} SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR GOODWILL. OUR AGGREGATE LIABILITY RELATING TO THE SERVICE SHALL NOT EXCEED THE AMOUNTS PAID BY YOU TO {company} IN THE 12 MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">14. Indemnification</h3>
                <p className="text-gray-700 mb-6">
                  You will defend, indemnify, and hold harmless {company} and its affiliates, officers, directors, employees, and agents from any claims, damages, liabilities, and expenses (including reasonable legal fees) arising from your use of the Service, your Customer Data, or your violation of these Terms or applicable law.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">15. Export Control & Sanctions</h3>
                <p className="text-gray-700 mb-6">
                  You represent that you are not located in, under the control of, or a national/resident of any country or entity subject to sanctions or export restrictions. You agree to comply with applicable export, re‑export, and sanctions laws and regulations.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">16. Term; Suspension; Termination</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                  <li>These Terms remain in effect until terminated.</li>
                  <li>You may stop using the Service at any time. Fees already paid are non‑refundable unless required by law.</li>
                  <li>We may suspend or terminate access for breach, legal risk, non‑payment, or misuse.</li>
                  <li>Upon termination, your right to access the Service ends. We will handle Customer Data deletion per our Privacy Policy and Data Security commitments.</li>
                </ul>

                <h3 className="text-xl font-bold text-gray-900 mb-4">17. Governing Law; Venue</h3>
                <p className="text-gray-700 mb-6">
                  These Terms are governed by the laws of the Province of Ontario and the federal laws of Canada applicable therein, without regard to conflicts of law principles. The courts located in Toronto, Ontario shall have exclusive jurisdiction over any dispute arising out of or relating to the Service or these Terms.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">18. Force Majeure</h3>
                <p className="text-gray-700 mb-6">
                  Neither party is liable for delay or failure to perform due to events beyond reasonable control (e.g., natural disasters, strikes, internet or cloud provider outages, war, government actions), provided the affected party uses reasonable efforts to mitigate.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">19. Changes to Terms</h3>
                <p className="text-gray-700 mb-6">
                  We may revise these Terms from time to time. If changes are material, we will provide notice (e.g., by email or in‑app). Your continued use of the Service after the effective date constitutes acceptance of the revised Terms.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">20. Contact Information</h3>
                <p className="text-gray-700">
                  For questions about these Terms, contact us at:<br />
                  Email: {legalEmail}<br />
                  Address: {companyAddress}
                </p>

                {(site?.legal?.dpaUrl || site?.legal?.slaUrl || site?.legal?.subprocessorsUrl) && (
                  <div className="mt-8 p-4 bg-gray-50 border rounded">
                    <h4 className="text-gray-900 font-semibold mb-2">Related documents</h4>
                    <ul className="list-disc pl-6 text-sm text-gray-700 space-y-1">
                      {site?.legal?.dpaUrl && (
                        <li><a className="text-blue-600 hover:underline" href={site.legal.dpaUrl} target="_blank" rel="noreferrer">Data Processing Addendum (DPA)</a></li>
                      )}
                      {site?.legal?.slaUrl && (
                        <li><a className="text-blue-600 hover:underline" href={site.legal.slaUrl} target="_blank" rel="noreferrer">Service Level (SLA)</a></li>
                      )}
                      {site?.legal?.subprocessorsUrl && (
                        <li><a className="text-blue-600 hover:underline" href={site.legal.subprocessorsUrl} target="_blank" rel="noreferrer">Sub‑processors</a></li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'privacy' && (
              <div className="prose max-w-none text-left">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Privacy Policy</h2>
                <p className="text-gray-600 mb-6">
                  <strong>Effective Date:</strong> {lastUpdated}<br />
                  <strong>Last Updated:</strong> {lastUpdated}
                </p>

                <p className="text-gray-700 mb-6 text-sm">
                  This Privacy Policy explains how {company} collects, uses, discloses, and protects personal information. We operate from Ontario, Canada and adhere to applicable privacy laws, including the Personal Information Protection and Electronic Documents Act (PIPEDA). Where we act as a processor/service provider on your behalf, our processing is governed by our agreements with you.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">1. Information We Collect</h3>
                <div className="text-gray-700 mb-6">
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Account & Business Information:</strong> name, business name, address, email, phone, billing details, service offerings, pricing, staff and hours.</li>
                    <li><strong>Customer & Booking Information:</strong> end‑customer names, contact details, preferences, appointment history, and communications.</li>
                    <li><strong>Communications Content:</strong> WhatsApp/SMS/chat messages, transcripts, call metadata, and—if you enable call recording—audio recordings (with consent where required).</li>
                    <li><strong>Integration Data:</strong> calendar data (event titles, times, attendees), third‑party IDs and tokens, configuration settings.</li>
                    <li><strong>Usage & Device Data:</strong> log files, IP address, browser type, pages viewed, timestamps, and cookies or similar technologies.</li>
                    <li><strong>Support Interactions:</strong> messages, attachments, and notes you share with our support team.</li>
                  </ul>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-4">2. How We Use Information</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                  <li>Provide, maintain, and improve the Service and develop new features.</li>
                  <li>Authenticate users, secure accounts, and prevent abuse.</li>
                  <li>Send transactional messages and administrative notices.</li>
                  <li>Support AI/automation to draft replies, classify intents, and schedule bookings.</li>
                  <li>Provide customer support and resolve issues.</li>
                  <li>Comply with legal obligations and enforce our Terms.</li>
                  <li>With consent where required, send marketing communications (opt‑out anytime).</li>
                </ul>

                <h3 className="text-xl font-bold text-gray-900 mb-4">3. Legal Bases (where applicable)</h3>
                <p className="text-gray-700 mb-6">
                  Depending on your location, we process personal information under one or more of the following bases: consent; performance of a contract; legitimate interests (e.g., Service security, product improvement); compliance with legal obligations.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">4. CASL & Messaging Consent</h3>
                <p className="text-gray-700 mb-6">
                  If you use our messaging features in Canada, you must obtain valid consent under Canada’s Anti‑Spam Legislation (CASL) and provide clear unsubscribe/opt‑out mechanisms. You are responsible for your contact lists and message content. Where applicable in other jurisdictions (e.g., TCPA in the U.S.), you must also obtain required consent.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">5. Sharing & Disclosures</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                  <li><strong>Service Providers/Sub‑processors:</strong> we use trusted vendors to host, process, and support the Service (e.g., cloud infrastructure, communications, analytics). We require appropriate safeguards and confidentiality commitments.</li>
                  <li><strong>Legal & Safety:</strong> we may disclose information to comply with law, respond to lawful requests, or protect rights, property, or safety of {company}, our users, or the public.</li>
                  <li><strong>Business Transfers:</strong> in a merger, acquisition, financing, or sale of assets, information may be transferred as part of the transaction subject to confidentiality.
                  </li>
                </ul>

                {(site?.legal?.subprocessorsUrl || site?.legal?.cookiesPolicyUrl) && (
                  <div className="text-gray-700 mb-6">
                    <ul className="list-disc pl-6 space-y-2">
                      {site?.legal?.subprocessorsUrl && (
                        <li>See our current list of sub‑processors: <a className="text-blue-600 hover:underline" href={site.legal.subprocessorsUrl} target="_blank" rel="noreferrer">Sub‑processors</a></li>
                      )}
                      {site?.legal?.cookiesPolicyUrl && (
                        <li>Learn more about cookies and similar technologies in our <a className="text-blue-600 hover:underline" href={site.legal.cookiesPolicyUrl} target="_blank" rel="noreferrer">Cookie Policy</a>.</li>
                      )}
                    </ul>
                  </div>
                )}

                <h3 className="text-xl font-bold text-gray-900 mb-4">6. International Transfers</h3>
                <p className="text-gray-700 mb-6">
                  We may process and store information in Canada, the United States, or other countries where we or our providers operate. We implement safeguards appropriate to the nature of the data and the transfer (e.g., contractual protections such as standard contractual clauses, and access controls). Some jurisdictions may have different data protection rules than your own.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">7. Data Retention</h3>
                <p className="text-gray-700 mb-6">
                  We retain personal information for as long as necessary to provide the Service, comply with legal obligations, resolve disputes, and enforce agreements. You may request deletion of Customer Data as described below; certain data may be retained in backups for a limited period.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">8. Your Rights</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                  <li><strong>Access & Correction (PIPEDA):</strong> you may request access to and correction of personal information under our control.</li>
                  <li><strong>GDPR/UK GDPR:</strong> subject to conditions, you may have rights to access, rectification, erasure, restriction, portability, and objection.</li>
                  <li><strong>CCPA/CPRA (California):</strong> California residents may have rights to know, delete, correct, and opt‑out of selling/sharing of personal information. We do not sell personal information as defined by the CCPA/CPRA.</li>
                  <li><strong>LGPD (Brazil):</strong> you may have rights to confirm processing, access, correction, anonymization, portability, deletion, and information on data‑sharing.</li>
                  <li><strong>Withdrawal of Consent:</strong> where processing is based on consent, you may withdraw it at any time (this will not affect prior processing).</li>
                  <li><strong>Marketing Opt‑Out:</strong> you can opt out of marketing communications by following unsubscribe instructions.</li>
                </ul>

                <h3 className="text-xl font-bold text-gray-900 mb-4">9. Children’s Privacy</h3>
                <p className="text-gray-700 mb-6">
                  The Service is not directed to children under the age of 13 (or the age of digital consent in your jurisdiction), and we do not knowingly collect personal information from them.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">10. Security</h3>
                <p className="text-gray-700 mb-6">
                  We employ administrative, technical, and physical safeguards proportionate to the sensitivity of the information we process. See the “Data Security” tab for details.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">11. Data Subject Requests</h3>
                <p className="text-gray-700 mb-6">
                  To submit a privacy request, contact us at <span className="font-medium">{legalEmail}</span>. We may need to verify your identity and your relationship to an account. If we process data on behalf of a business customer, we will direct you to that business where appropriate.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">12. Complaints</h3>
                <p className="text-gray-700 mb-6">
                  You may contact us with any concerns. You also have the right to lodge a complaint with the Office of the Privacy Commissioner of Canada or your provincial privacy commissioner, your local data protection authority in the EEA/UK, or other supervisory authority as applicable.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">13. Representatives & DPO (if applicable)</h3>
                <p className="text-gray-700 mb-6 text-sm">
                  If we appoint an EU/UK representative or a Data Protection Officer, we will list their contact details here or in our DPA.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">14. Changes to this Policy</h3>
                <p className="text-gray-700 mb-6">
                  We may update this Privacy Policy periodically. Material changes will be notified (e.g., by email or in‑app). Continued use after changes take effect indicates acceptance.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">15. Contact Us</h3>
                <p className="text-gray-700">
                  Email: {legalEmail}<br />
                  Address: {companyAddress}
                </p>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="prose max-w-none text-left">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Data Security</h2>

                <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
                  <div className="flex items-center mb-4">
                    <Shield className="h-6 w-6 text-green-600 mr-3" />
                    <h3 className="text-lg font-medium text-green-900">Enterprise‑Grade Security</h3>
                  </div>
                  <p className="text-green-800">
                    We implement layered administrative, technical, and physical controls to protect Customer Data in accordance with industry best practices and the sensitivity of the information we process.
                  </p>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-4">1. Security Program</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                  <li>Formal policies for access control, acceptable use, encryption, vulnerability management, incident response, and business continuity.</li>
                  <li>Employee security training and confidentiality obligations.</li>
                  <li>Least‑privilege access; role‑based permissions; MFA for production systems.</li>
                </ul>

                <h3 className="text-xl font-bold text-gray-900 mb-4">2. Infrastructure & Network</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                  <li>Hosting on reputable cloud providers with certified facilities.</li>
                  <li>Segregated environments, VPC/network segmentation, and firewalls.</li>
                  <li>Continuous monitoring, centralized logging, and alerting.</li>
                </ul>

                <h3 className="text-xl font-bold text-gray-900 mb-4">3. Encryption</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                  <li>Encryption in transit (TLS 1.2+).</li>
                  <li>Encryption at rest for primary data stores and backups.</li>
                  <li>Secure key management following cloud‑provider best practices.</li>
                </ul>

                <h3 className="text-xl font-bold text-gray-900 mb-4">4. Application Security</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                  <li>Secure SDLC, code reviews, and dependency scanning.</li>
                  <li>Regular vulnerability scanning and remediation.</li>
                  <li>Third‑party penetration testing periodically; results tracked to resolution.</li>
                </ul>

                <h3 className="text-xl font-bold text-gray-900 mb-4">5. Data Management</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                  <li>Tenant separation and logical data segregation.</li>
                  <li>Backups performed regularly and tested periodically.</li>
                  <li>Data retention and deletion aligned with the Privacy Policy and customer agreements.</li>
                </ul>

                <h3 className="text-xl font-bold text-gray-900 mb-4">6. Business Continuity & Disaster Recovery</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                  <li>Documented plans for continuity and disaster recovery.</li>
                  <li>Redundancy for critical components and routine recovery testing.</li>
                </ul>

                <h3 className="text-xl font-bold text-gray-900 mb-4">7. Incident Response</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                  <li>Documented incident response procedures with defined roles.</li>
                  <li>Prompt investigation and remediation of suspected incidents.</li>
                  <li>Legal/contractual notifications will be made without undue delay and in accordance with applicable law and our agreements.</li>
                </ul>

                <h3 className="text-xl font-bold text-gray-900 mb-4">8. Sub‑processors</h3>
                {site?.legal?.subprocessorsUrl ? (
                  <p className="text-gray-700 mb-6">
                    We maintain a list of current sub‑processors that support delivery of the Service. See: {' '}
                    <a className="text-blue-600 hover:underline" href={site.legal.subprocessorsUrl} target="_blank" rel="noreferrer">Sub‑processors</a>.
                  </p>
                ) : (
                  <p className="text-gray-700 mb-6">
                    We use reputable service providers (e.g., cloud hosting, communications) under written contracts with confidentiality and security obligations.
                  </p>
                )}

                <h3 className="text-xl font-bold text-gray-900 mb-4">9. Compliance & Requests</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                  <li>We can provide security and compliance information to customers under NDA upon request.</li>
                  <li>If required, a Data Processing Addendum (DPA) is available. {site?.legal?.dpaUrl && (<span>Download the DPA <a className="text-blue-600 hover:underline" href={site.legal.dpaUrl} target="_blank" rel="noreferrer">here</a>.</span>)}</li>
                </ul>

                <h3 className="text-xl font-bold text-gray-900 mb-4">10. Report Security Issues</h3>
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <h4 className="font-medium text-red-900 mb-2">Found a Security Issue?</h4>
                  <p className="text-red-800 mb-3">
                    We take security seriously and appreciate responsible disclosure of any vulnerabilities.
                  </p>
                  <p className="text-red-800">
                    Contact our security team: <strong>{securityEmail}</strong><br />
                    For urgent issues: <strong>{supportPhone}</strong>
                  </p>
                </div>

                <p className="text-gray-500 text-sm mt-6">Last updated: {lastUpdated}</p>
              </div>
            )}

            {activeTab === 'cookies' && (
              <div className="prose max-w-none text-left">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Cookie Policy</h2>
                <p className="text-gray-600 mb-6">
                  <strong>Effective Date:</strong> {lastUpdated}<br />
                  <strong>Last Updated:</strong> {lastUpdated}
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">1. What Are Cookies?</h3>
                <p className="text-gray-700 mb-6">
                  Cookies are small text files placed on your device when you visit our website. They help us provide core functionality, enhance user experience, analyze usage, and deliver relevant advertising. Similar technologies (e.g., pixels, local storage, SDKs) are treated like cookies in this Policy.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">2. Types of Cookies We Use</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                  <li><strong>Strictly Necessary:</strong> Required for the site to function (e.g., login, session management, fraud prevention). These cannot be switched off in our systems.</li>
                  <li><strong>Performance & Analytics:</strong> Measure site performance and usage (e.g., Google Analytics). Data is aggregated where possible.</li>
                  <li><strong>Functional:</strong> Remember preferences (e.g., language, region, theme) and improve personalization.</li>
                  <li><strong>Advertising/Targeting:</strong> Deliver personalized ads and measure campaign effectiveness across sites.</li>
                </ul>

                <h3 className="text-xl font-bold text-gray-900 mb-4">3. Third‑Party Cookies</h3>
                <p className="text-gray-700 mb-6">
                  Some cookies are set by third parties, such as analytics providers or advertising networks. These parties may use cookies to build a profile of your interests and show you relevant ads on other sites. We recommend reviewing their privacy policies for details.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">4. Managing Cookies & Consent</h3>
                <p className="text-gray-700 mb-4">
                  You can manage cookies through your browser settings. Most browsers allow you to block or delete cookies. If you disable cookies, certain features of our Service may not function properly.
                </p>
                <p className="text-gray-700 mb-6">
                  Where required by law (e.g., GDPR/UK GDPR, ePrivacy rules), we will seek explicit consent before placing non‑essential cookies via a consent banner or preference center. You can update your preferences at any time from the banner link in the footer (if available).
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">5. Retention</h3>
                <p className="text-gray-700 mb-6">
                  Cookie lifespans vary. Session cookies expire when you close your browser. Persistent cookies remain until they expire or are deleted. Specific durations depend on the cookie and provider.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">6. Do Not Track</h3>
                <p className="text-gray-700 mb-6">
                  Some browsers offer a “Do Not Track” (DNT) signal. There is no common standard for DNT; therefore, we do not respond to DNT at this time.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">7. Updates to this Policy</h3>
                <p className="text-gray-700 mb-6">
                  We may update this Cookie Policy to reflect changes in technology, legal requirements, or our practices. We encourage you to review this page periodically.
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">8. Contact Us</h3>
                <p className="text-gray-700">
                  Email: {legalEmail}<br />
                  Address: {companyAddress}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LegalPages;
